/**
 * Get possible parent domains for cookie operations
 * For example: staging.gtintel.io -> ['.gtintel.io', '.staging.gtintel.io']
 * This helps clear cookies that may have been set at different domain levels
 */
export function getPossibleCookieDomains() {
  // Return empty array for server-side rendering
  if (typeof window === 'undefined') {
    return [];
  }
  const hostname = window.location.hostname;
  const domains = [];

  // Always try to remove from current domain (undefined means current domain)
  domains.push(undefined);

  // For localhost, add common variations
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domains.push('localhost');
    domains.push('.localhost');
    return domains;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // Generate possible parent domains
  // For staging.gtintel.io, this generates: .gtintel.io, .staging.gtintel.io
  // For app.staging.gtintel.io, this generates: .gtintel.io, .staging.gtintel.io, .app.staging.gtintel.io
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = '.' + parts.slice(i).join('.');
    domains.push(domain);
  }

  // Also add the current hostname without a dot prefix
  domains.push(hostname);

  // Filter out undefined and return unique values
  return Array.from(new Set(domains.filter(d => d !== undefined)));
}

/**
 * Remove a cookie from all possible domains
 * @param cookieName - The name of the cookie to remove
 * @param options - Additional options for cookie removal
 */
export function removeCookieFromAllDomains(cookieName, options) {
  // Only run in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Dynamically import js-cookie only when needed (browser-side)
  import('js-cookie').then(module => {
    const Cookies = module.default;
    const path = options?.path || '/';
    const possibleDomains = getPossibleCookieDomains();

    // Try removing from current domain first (most specific)
    Cookies.remove(cookieName);
    Cookies.remove(cookieName, {
      path
    });

    // Then try all possible parent domains
    possibleDomains.forEach(domain => {
      if (domain) {
        Cookies.remove(cookieName, {
          domain,
          path
        });
      }
    });
  });
}